'use client';

/* eslint-disable @next/next/no-img-element */
/* eslint-disable jsx-a11y/alt-text */
import { useMemo, useState } from 'react';
import { LazyLoadImage, LazyLoadImageProps } from 'react-lazy-load-image-component';
import 'react-lazy-load-image-component/src/effects/blur.css';

export type ImageProps = {
  ref?: React.RefObject<HTMLImageElement | null>;
  format?: 'webp' | 'jpg' | 'png';
  useRetina?: boolean;
  quality?: number;
  fetchPriority?: 'high' | 'low' | 'auto';
  errorFallback?: React.ReactNode;
  // Only included to separate this from props
  height?: string | number;
  lazyLoad?: boolean;
} & LazyLoadImageProps;

const Image: React.FC<ImageProps> = ({
  src,
  alt,
  quality = 83,
  format = 'webp',
  useRetina = true,
  width,
  errorFallback,
  height,
  lazyLoad,
  ...props
}) => {
  const [error, setError] = useState(false);

  const baseWidth = Math.min(Number(width) || 600, 1400);
  const parsedWidth = !!useRetina ? Number(baseWidth) * 2 : baseWidth;

  const isCtf = src?.includes('ctfassets');
  const isLocalImage = typeof src === 'string' && src.startsWith('/');
  const isDataUri = typeof src === 'string' && src.startsWith('data:');

  const url = useMemo(() => {
    // Skip query params for local images
    if (isLocalImage || isDataUri) {
      return src;
    }

    const hasQuery = src?.includes('?');
    const srcParamsSeparator = hasQuery ? '&' : '?';

    if (typeof src === 'string' && isCtf) {
      return `${src}${srcParamsSeparator}q=${quality}&w=${parsedWidth}&fm=${format}`;
    }

    if (typeof src === 'string') {
      return `${src}${srcParamsSeparator}quality=${quality}&width=${parsedWidth}&format=${format}`;
    }

    return '';
  }, [src, quality, parsedWidth, format, isCtf, isLocalImage, isDataUri]);

  if (!src) {
    return null;
  }

  if (errorFallback && error) {
    return errorFallback;
  }

  // When srcSet is provided, don't pass width/height as HTML attributes
  const { srcSet, sizes, ...restProps } = props as typeof props & {
    srcSet?: string;
    sizes?: string;
  };
  const shouldOmitDimensions = !!srcSet;

  const imageProps = {
    src: url || src,
    alt,
    onError: () => setError(true),
    srcSet,
    sizes,
    ...restProps,
    // Only include width/height if srcSet is not provided
    ...(shouldOmitDimensions ? {} : { width, height }),
  };

  return lazyLoad ? <LazyLoadImage {...imageProps} /> : <img {...imageProps} />;
};

export default Image;
